/**
 * One place that turns ANY thrown thing into a sentence a person can read.
 *
 * THE BUG THIS EXISTS FOR. The platform's error envelope is not one shape.
 * Different services answer with `error` as a string, as `{ message }`, or as
 * `{ title, description }`. The idiom that spread through this codebase —
 *
 *     err?.response?.data?.error || err?.message || 'Something went wrong'
 *
 * — returns the OBJECT whenever the envelope is the object shape, because an
 * object is truthy. That value then lands in React state, or in a feedback
 * toast, and is rendered as a JSX child, which throws:
 *
 *     Objects are not valid as a React child (found: object with keys
 *     {description, title})
 *
 * A 500 on one small pane took down the whole page it was docked in.
 *
 * The fix is not "check this one call site". It is that the candidates are
 * walked in order of how specific they are, and each one is ACCEPTED ONLY IF
 * IT IS A NON-EMPTY STRING. An object anywhere in the chain is skipped rather
 * than returned, and the last resort is a literal — so it is impossible for
 * these helpers to hand a caller anything but a string, for any error shape,
 * including shapes no service sends yet.
 */

/** A string if the value already is one (and says something), else ''. */
const asText = (value: unknown): string =>
  typeof value === 'string' && value.trim() !== '' ? value : '';

/**
 * Best-effort text out of a lone error-ish value — an envelope's `error`
 * field, a rejected thunk's payload, a PubSub event's `error`.
 *
 * Strings pass through. Objects are read for the text they carry
 * (`message` / `description` / `title` / `detail`) rather than stringified,
 * because `String({title, description})` is "[object Object]" — which is not
 * a crash, but is not a sentence either. Anything else yields ''.
 */
export const toErrorText = (value: unknown): string => {
  const direct = asText(value);
  if (direct) return direct;
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    return (
      asText(obj.message) ||
      asText(obj.description) ||
      asText(obj.title) ||
      asText(obj.detail) ||
      asText(obj.error)
    );
  }
  return '';
};

/**
 * ANY thrown thing → a display string. Never an object, never undefined.
 *
 * This is the replacement for the fragile `|| err?.response?.data?.error ||`
 * chains: same intent, same ordering of preferences, but every candidate must
 * be a string to be chosen.
 */
export const readErrorMessage = (err: any, fallback: string): string => {
  const data = err?.response?.data;
  const candidates = [
    data?.error?.message,
    data?.error?.description,
    data?.error?.title,
    data?.error,
    data?.message,
    data?.description,
    data?.title,
    data?.detail,
    err?.message,
  ];
  const found = candidates.find(
    (value) => typeof value === 'string' && value.trim() !== ''
  );
  if (found) return found as string;

  // Nothing string-shaped in the usual slots. Dig for text inside whichever
  // object-shaped envelope is present before giving up on the fallback.
  return toErrorText(data?.error) || toErrorText(data) || toErrorText(err) || fallback;
};

/**
 * The last line of defence, for render sinks (toasts, alerts, error panes)
 * that receive an already-extracted "message" from somewhere they do not
 * control. Guarantees a string so a bad caller degrades to a plain message
 * instead of unmounting the tree.
 */
export const ensureDisplayString = (value: unknown, fallback = ''): string =>
  asText(value) || toErrorText(value) || fallback;

export default readErrorMessage;
